"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type VoiceOption = {
  name: string;
  lang: string;
  voice: SpeechSynthesisVoice;
};

export default function TTSReader() {
  const supportsTTS =
    typeof window !== "undefined" && "speechSynthesis" in window;

  const [text, setText] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>("");
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Load available voices
  useEffect(() => {
    if (!supportsTTS) return;

    function refreshVoices() {
      const list = window.speechSynthesis.getVoices();
      const options: VoiceOption[] = list.map((v) => ({
        name: v.name,
        lang: v.lang,
        voice: v,
      }));
      setVoices(options);
      if (!selectedVoice && options.length > 0) {
        // Prefer an English voice by default if available
        const preferred =
          options.find((v) => v.lang.toLowerCase().startsWith("en")) ||
          options[0];
        setSelectedVoice(preferred.name);
      }
    }

    refreshVoices();
    window.speechSynthesis.addEventListener("voiceschanged", refreshVoices);
    return () =>
      window.speechSynthesis.removeEventListener(
        "voiceschanged",
        refreshVoices
      );
  }, [supportsTTS, selectedVoice]);

  // Keep text synced with current selection if any
  useEffect(() => {
    function handleSelectionChange() {
      const sel = window.getSelection?.();
      const value = sel?.toString() ?? "";
      if (value && value.trim().length > 0) {
        setText(value.trim());
      }
    }
    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  const currentVoice = useMemo(
    () => voices.find((v) => v.name === selectedVoice)?.voice,
    [voices, selectedVoice]
  );

  const canSpeak = supportsTTS && text.trim().length > 0;

  function stopSpeaking() {
    try {
      window.speechSynthesis.cancel();
    } catch {}
    utteranceRef.current = null;
    setIsSpeaking(false);
    setIsPaused(false);
  }

  function speak() {
    if (!supportsTTS || !canSpeak) return;
    stopSpeaking();
    const u = new SpeechSynthesisUtterance(text);
    if (currentVoice) u.voice = currentVoice;
    u.onstart = () => setIsSpeaking(true);
    u.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };
    u.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };
    utteranceRef.current = u;
    window.speechSynthesis.speak(u);
  }

  function pause() {
    if (!supportsTTS) return;
    try {
      window.speechSynthesis.pause();
      setIsPaused(true);
    } catch {}
  }

  function resume() {
    if (!supportsTTS) return;
    try {
      window.speechSynthesis.resume();
      setIsPaused(false);
    } catch {}
  }

  if (!supportsTTS) {
    return (
      <div className="p-4 text-sm">
        Your browser does not support Text-to-Speech.
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <label className="text-sm text-foreground/80">Voice</label>
        <select
          className="min-w-[220px] rounded-md border border-border bg-surface px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
        >
          {voices.map((v) => (
            <option
              key={v.name}
              value={v.name}
            >{`${v.name} (${v.lang})`}</option>
          ))}
        </select>
      </div>

      <div className="flex-1">
        <label htmlFor="tts-textarea" className="sr-only">
          Text to read
        </label>
        <textarea
          id="tts-textarea"
          className="h-full w-full resize-none rounded-lg border border-border bg-background p-3 text-sm leading-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          placeholder="Select text on the page to auto-fill, or type/paste here..."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <button
            className="h-9 rounded-md bg-accent px-4 text-sm text-accent-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={speak}
            disabled={!canSpeak || isSpeaking}
            aria-disabled={!canSpeak || isSpeaking}
            aria-label="Play"
          >
            Play
          </button>
          <button
            className="h-9 rounded-md bg-surface px-4 text-sm hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={isPaused ? resume : pause}
            disabled={!isSpeaking}
            aria-disabled={!isSpeaking}
            aria-label={isPaused ? "Resume" : "Pause"}
          >
            {isPaused ? "Resume" : "Pause"}
          </button>
          <button
            className="h-9 rounded-md bg-surface px-4 text-sm hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={stopSpeaking}
            disabled={!isSpeaking && !isPaused}
            aria-disabled={!isSpeaking && !isPaused}
            aria-label="Stop"
          >
            Stop
          </button>
        </div>

        <div className="text-xs text-foreground/70">
          {isSpeaking
            ? isPaused
              ? "Paused"
              : "Playing"
            : text
            ? "Ready"
            : "Enter or select text"}
        </div>
      </div>
    </div>
  );
}
